import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Collapse,
  Paper,
  CircularProgress,
  Divider,
  Chip,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import RateReviewIcon from "@material-ui/icons/RateReview";
import CloseIcon from "@material-ui/icons/Close";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import FilterListIcon from "@material-ui/icons/FilterList";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import SchoolIcon from "@material-ui/icons/School";
import BusinessIcon from "@material-ui/icons/Business";
import RefreshIcon from "@material-ui/icons/Refresh";
import TeacherReportService from "./Locality/Service/teacherReportService";
import AddClassService from "./Locality/Service/addClassService";
import SectionService from "./Locality/Service/sectionService";
import SchooleRegistrationService from "./Locality/Service/schooleRegistration";
import AcademicYearService from "./Locality/Service/academicYearService";
import TeacherService from "./Locality/Service/teacherService";

const normalizeId = (value) => {
  if (!value) return "";
  if (typeof value === "object" && value._id) return String(value._id);
  return String(value);
};

const getSchoolId = (ud) =>
  normalizeId(ud?.schooleId?._id || ud?.schooleId || ud?.schoolId);

// UI starts at 0; saving requires at least 1 question.
const MIN_Q = 0;
const MIN_Q_TO_SAVE = 1;
const MIN_OPTIONS = 2;

let questionClientIdSeq = 0;
function newQuestionClientId() {
  questionClientIdSeq += 1;
  return `q-${Date.now()}-${questionClientIdSeq}`;
}

/** New questions start with empty choice fields (admin fills before save). */
const DEFAULT_OPTION_LABELS = ["", "", "", ""];

function normalizeOptions(raw) {
  if (Array.isArray(raw) && raw.length > 0) {
    const mapped = raw.map((o) => String(o ?? ""));
    return mapped.length >= MIN_OPTIONS ? mapped : [...mapped, ...Array(MIN_OPTIONS - mapped.length).fill("")];
  }
  return [...DEFAULT_OPTION_LABELS];
}

function normalizeQuestion(q, index) {
  return {
    ...q,
    _clientId: q._clientId || newQuestionClientId(),
    order: index,
    text: q.text || `Question ${index + 1}`,
    options: normalizeOptions(q.options),
  };
}

function createDefaultQuestion(index) {
  return normalizeQuestion(
    {
      text: `Question ${index + 1}`,
      options: [...DEFAULT_OPTION_LABELS],
    },
    index,
  );
}

function extractActiveAcademicYearOptions(list) {
  const rows = (Array.isArray(list) ? list : [])
    .filter((r) => String(r.status || "Active").toLowerCase() === "active")
    .sort((a, b) => {
      const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
      return bTime - aTime;
    });
  const seen = new Set();
  const unique = [];
  rows.forEach((r) => {
    const label = String(r.acadamicYear || "").trim();
    if (label && !seen.has(label)) {
      seen.add(label);
      unique.push(label);
    }
  });
  return unique;
}

const useRatingsTableStyles = makeStyles({
  tableCard: {
    borderRadius: 14,
    border: "1px solid #e6eaf0",
    boxShadow: "0 2px 10px rgba(16,42,67,0.06)",
    overflow: "hidden",
    marginTop: 16,
  },
  headCell: {
    backgroundColor: "#f4f6fa",
    color: "#46566b",
    fontWeight: 700,
    fontSize: "0.8rem",
    borderBottom: "1px solid #e6eaf0",
    whiteSpace: "nowrap",
  },
  bodyCell: {
    fontSize: "0.875rem",
  },
  ratedChip: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    fontWeight: 600,
    height: 24,
  },
  notRatedChip: {
    backgroundColor: "#ffebee",
    color: "#c62828",
    fontWeight: 600,
    height: 24,
  },
});

const usePageStyles = makeStyles((theme) => ({
  pageHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: "1.9rem",
    color: "#0f2d4d",
    lineHeight: 1.2,
  },
  pageSubtitle: {
    color: "#6b7280",
    fontSize: "0.9rem",
    marginTop: 2,
  },
  noteBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderRadius: 12,
    backgroundColor: "#eef4ff",
    border: "1px solid #d6e4ff",
    marginBottom: theme.spacing(2),
  },
  noteIconWrap: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    backgroundColor: "#1769d6",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  filtersCard: {
    borderRadius: 14,
    border: "1px solid #e6eaf0",
    boxShadow: "0 2px 10px rgba(16,42,67,0.06)",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  filtersTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  filtersChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 14px",
    borderRadius: 8,
    backgroundColor: "#f4f6fa",
    border: "1px solid #e6eaf0",
    cursor: "pointer",
    fontWeight: 600,
    color: "#0f2d4d",
    fontSize: "0.9rem",
  },
  countBadge: {
    backgroundColor: "#1769d6",
    color: "#fff",
    borderRadius: 10,
    fontSize: "0.72rem",
    fontWeight: 700,
    minWidth: 18,
    height: 18,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 5px",
  },
  fieldLabel: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#46566b",
    marginBottom: 6,
  },
  primaryBtn: {
    backgroundColor: "#1769d6",
    color: "#fff",
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    padding: "8px 18px",
    boxShadow: "0 4px 10px rgba(23,105,214,0.25)",
    "&:hover": { backgroundColor: "#125bbd" },
  },
  contentCard: {
    borderRadius: 14,
    border: "1px solid #e6eaf0",
    boxShadow: "0 2px 10px rgba(16,42,67,0.06)",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  dialogPaper: {
    borderRadius: 14,
    width: 480,
    maxWidth: "95vw",
  },
  dialogTitleBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 2.5),
    borderBottom: "1px solid #eef1f5",
  },
  dialogTitleText: {
    fontWeight: 700,
    fontSize: "1.15rem",
    color: "#0f2d4d",
  },
  dialogBody: {
    padding: theme.spacing(2.5),
  },
  dialogFooter: {
    padding: theme.spacing(1.5, 2.5),
    borderTop: "1px solid #eef1f5",
  },
  cancelBtn: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    color: "#46566b",
  },
  saveBtn: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    backgroundColor: "#1769d6",
    color: "#fff",
    boxShadow: "0 4px 10px rgba(23,105,214,0.25)",
    "&:hover": { backgroundColor: "#125bbd" },
  },
}));

const useTemplateStyles = makeStyles((theme) => ({
  optionsHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  optionsToolbar: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #cfd8dc",
    borderRadius: 8,
    backgroundColor: "#fafafa",
  },
  optionRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  optionField: {
    flex: 1,
  },
  optionRemoveBtn: {
    marginTop: 4,
    border: "1px solid #ffcdd2",
    borderRadius: 8,
    backgroundColor: "#fff5f5",
  },
  optionAddBtn: {
    border: "1px solid #c8e6c9",
    borderRadius: 8,
    backgroundColor: "#f1f8f4",
  },
  choiceChip: {
    display: "inline-block",
    padding: "4px 10px",
    marginRight: 8,
    marginBottom: 6,
    borderRadius: 16,
    backgroundColor: "#e8f0fe",
    color: "#1565c0",
    fontSize: "0.8125rem",
  },
  addQuestionRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(2),
  },
}));

export default function TeacherReportRatings() {
  const ratingsTableClasses = useRatingsTableStyles();
  const pageClasses = usePageStyles();
  const templateClasses = useTemplateStyles();
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userDetail") || "{}");
    } catch {
      return {};
    }
  }, []);
  const role = user.role || "";
  const schoolId = getSchoolId(user);
  const isTeacher = role === "TEACHER";

  const canEditTemplate = role === "SCHOOLE" || role === "SUPERADMIN";
  const canViewRatings = role === "TEACHER" || role === "SCHOOLE" || role === "SUPERADMIN";
  const canSubmitRating = role === "TEACHER" || role === "SCHOOLE";

  const [tab, setTab] = useState(
    role === "TEACHER" ? "ratings" : canEditTemplate ? "template" : "ratings",
  );
  const [superAdminSchoolId, setSuperAdminSchoolId] = useState("");
  const [schoolList, setSchoolList] = useState([]);

  const [template, setTemplate] = useState(null);
  const [questions, setQuestions] = useState([]);
  /** Same length as `questions`: true = show edit fields for that row */
  const [editingFlags, setEditingFlags] = useState([]);
  const [templateTitle, setTemplateTitle] = useState("");
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    clientId: null,
    label: "",
  });

  const [classList, setClassList] = useState([]);
  const [sectionList, setSectionList] = useState([]);
  const [classId, setClassId] = useState(isTeacher ? normalizeId(user.classId) : "");
  const [sectionId, setSectionId] = useState(isTeacher ? normalizeId(user.sectionId) : "");
  /** Reporting period: dropdown values = AcademicYear.js (Academic Master) rows via AcademicYearService.getAllAcademicYear — only status Active, field acadamicYear */
  const [academicYearOptions, setAcademicYearOptions] = useState([]);
  const [periodKey, setPeriodKey] = useState("");
  const [rows, setRows] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const [rateOpen, setRateOpen] = useState(false);
  const [rateStudent, setRateStudent] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [noteOpen, setNoteOpen] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const effectiveSchoolId = role === "SUPERADMIN" ? superAdminSchoolId : schoolId;

  const activeFilterCount =
    (role === "SUPERADMIN" && superAdminSchoolId ? 1 : 0) +
    (classId ? 1 : 0) +
    (sectionId ? 1 : 0) +
    (periodKey ? 1 : 0);

  const loadSectionsForClass = (selectedClassId) => {
    const sid = role === "SUPERADMIN" ? superAdminSchoolId : schoolId;
    if (!sid || !selectedClassId) {
      setSectionList([]);
      return Promise.resolve([]);
    }
    return SectionService.findSectionList({
      schooleId: sid,
      classId: selectedClassId,
    })
      .then((res) => {
        const rows = Array.isArray(res) ? res : [];
        setSectionList(rows);
        return rows;
      })
      .catch(() => {
        setSectionList([]);
        return [];
      });
  };

  const applyClassSection = (selectedClassId, preferredSectionId) => {
    const cid = normalizeId(selectedClassId);
    if (!cid) return Promise.resolve();
    setClassId(cid);
    return loadSectionsForClass(cid).then((rows) => {
      let validSection = "";
      if (rows.length === 1) {
        validSection = normalizeId(rows[0]._id);
      } else if (
        rows.length > 1 &&
        preferredSectionId &&
        rows.some((s) => normalizeId(s._id) === normalizeId(preferredSectionId))
      ) {
        validSection = normalizeId(preferredSectionId);
      }
      setSectionId(validSection);
      return { classId: cid, sectionId: validSection };
    });
  };

  const syncTeacherProfile = () => {
    if (!isTeacher) return Promise.resolve();
    const storedClassId = normalizeId(user.classId);
    const storedSectionId = normalizeId(user.sectionId);
    if (!user.teacherId) {
      if (storedClassId) return applyClassSection(storedClassId, storedSectionId);
      return Promise.resolve();
    }
    return TeacherService.getTeacherById(user.teacherId)
      .then((teacher) => {
        const classIds = new Set();
        const primaryClassId = normalizeId(teacher?.classId?._id || teacher?.classId);
        if (primaryClassId) classIds.add(primaryClassId);
        (teacher?.additionalClassAndSection || []).forEach((item) => {
          const cid = normalizeId(item?.class?.classId?._id || item?.class?.classId);
          if (cid) classIds.add(cid);
        });

        const freshClassId = primaryClassId || storedClassId;
        const freshSectionId =
          normalizeId(teacher?.sectionId?._id || teacher?.sectionId) || storedSectionId;
        const sectionCount = new Set();
        if (freshSectionId) sectionCount.add(freshSectionId);
        (teacher?.additionalClassAndSection || []).forEach((item) => {
          (item?.sectionId || []).forEach((s) => {
            const sid = normalizeId(s?.sectionId?._id || s?.sectionId);
            if (sid) sectionCount.add(sid);
          });
        });

        const preferredSection =
          classIds.size > 1 || sectionCount.size > 1 ? "" : freshSectionId;
        return applyClassSection(freshClassId, preferredSection);
      })
      .catch(() => {
        if (storedClassId) return applyClassSection(storedClassId, storedSectionId);
        return Promise.resolve();
      });
  };

  const handleClassChange = (e) => {
    const selectedClassId = e.target.value;
    if (!selectedClassId) {
      setClassId("");
      setSectionId("");
      setSectionList([]);
      return;
    }
    applyClassSection(selectedClassId, "");
  };

  const handleSectionChange = (e) => {
    setSectionId(e.target.value);
  };

  useEffect(() => {
    if (role !== "SUPERADMIN") return;
    SchooleRegistrationService.getAllSchooleRegistration()
      .then((res) => setSchoolList(Array.isArray(res) ? res : []))
      .catch(() => setSchoolList([]));
  }, [role]);

  const loadTemplate = () => {
    if (!effectiveSchoolId) {
      setTemplate(null);
      setQuestions([]);
      return;
    }
    setTemplateLoading(true);
    TeacherReportService.getTemplate({ schooleId: effectiveSchoolId })
      .then((doc) => {
        setTemplate(doc);
        setTemplateTitle(doc.title || "");
        const raw = Array.isArray(doc.questions) ? doc.questions : [];
        const normalized = raw.length > 0 ? raw.map((q, i) => normalizeQuestion(q, i)) : [];
        setQuestions(normalized);
        setEditingFlags(normalized.map(() => false));
      })
      .catch(() => {
        setTemplate(null);
        setQuestions([]);
      })
      .finally(() => setTemplateLoading(false));
  };

  useEffect(() => {
    loadTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveSchoolId]);

  useEffect(() => {
    if (!effectiveSchoolId) {
      setAcademicYearOptions([]);
      setPeriodKey("");
      return;
    }
    const applyYearOptions = (unique, preferredYear) => {
      setAcademicYearOptions(unique);
      setPeriodKey((prev) => {
        if (prev && unique.includes(prev)) return prev;
        if (preferredYear && unique.includes(preferredYear)) return preferredYear;
        return unique[0] || preferredYear || "";
      });
    };

    AcademicYearService.getAllAcademicYear(effectiveSchoolId)
      .then((res) => {
        let unique = extractActiveAcademicYearOptions(res);
        return AcademicYearService.getActiveFinancialYear()
          .then((activeRes) => {
            const defaultYear = String(activeRes?.acadamicYear || "").trim();
            if (defaultYear && !unique.includes(defaultYear)) {
              unique = [defaultYear, ...unique];
            }
            applyYearOptions(unique, defaultYear);
          })
          .catch(() => {
            applyYearOptions(unique, unique[0] || "");
          });
      })
      .catch(() => {
        AcademicYearService.getActiveFinancialYear()
          .then((activeRes) => {
            const defaultYear = String(activeRes?.acadamicYear || "").trim();
            if (defaultYear) {
              applyYearOptions([defaultYear], defaultYear);
            } else {
              setAcademicYearOptions([]);
              setPeriodKey("");
            }
          })
          .catch(() => {
            setAcademicYearOptions([]);
            setPeriodKey("");
          });
      });
  }, [effectiveSchoolId]);

  useEffect(() => {
    if (!schoolId && role !== "SUPERADMIN") return;
    const sid = role === "SUPERADMIN" ? superAdminSchoolId : schoolId;
    if (!sid) return;
    AddClassService.getAllAddClass(
      sid,
      isTeacher ? "TEACHER" : undefined,
      true,
    )
      .then((res) => {
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        setClassList(list);
        if (isTeacher) {
          return syncTeacherProfile();
        }
        if (role === "SCHOOLE" && list.length === 1 && !classId) {
          setClassId(normalizeId(list[0]._id));
        }
        return Promise.resolve();
      })
      .catch(() => setClassList([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, role, superAdminSchoolId]);

  useEffect(() => {
    const sid = role === "SUPERADMIN" ? superAdminSchoolId : schoolId;
    if (!sid || !classId) {
      if (!isTeacher) setSectionList([]);
      return;
    }
    if (isTeacher) return;
    loadSectionsForClass(classId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, role, superAdminSchoolId, classId, isTeacher]);

  const loadClassRatings = () => {
    if (!effectiveSchoolId || !classId || !periodKey) return;
    setListLoading(true);
    const params = {
      schooleId: effectiveSchoolId,
      classId,
      periodKey,
    };
    if (sectionId) params.sectionId = sectionId;
    TeacherReportService.listClassRatings(params)
      .then((data) => setRows(Array.isArray(data?.rows) ? data.rows : []))
      .catch((err) => {
        console.error(err);
        setRows([]);
      })
      .finally(() => setListLoading(false));
  };

  useEffect(() => {
    if (tab !== "ratings") return;
    if (!effectiveSchoolId || !classId) return;
    loadClassRatings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, effectiveSchoolId, classId, sectionId, periodKey]);

  const buildPayloadQuestions = (list) =>
    list.map((q, idx) => ({
      order: idx,
      text: String(q.text || "").trim(),
      options: (q.options || []).map((o) => String(o).trim()).filter(Boolean),
    }));

  const persistTemplate = (questionList) => {
    if (!effectiveSchoolId) {
      return Promise.reject(new Error("Select a school first."));
    }
    const list = questionList ?? questions;
    if (list.length < MIN_Q_TO_SAVE) {
      return Promise.reject(new Error(`Please add at least ${MIN_Q_TO_SAVE} question before saving.`));
    }
    for (let i = 0; i < list.length; i += 1) {
      const q = list[i];
      if (!String(q.text || "").trim()) {
        return Promise.reject(new Error(`Question ${i + 1} text is required.`));
      }
      const opts = (q.options || []).map((o) => String(o).trim()).filter(Boolean);
      if (opts.length < MIN_OPTIONS) {
        return Promise.reject(
          new Error(`Question ${i + 1} needs at least ${MIN_OPTIONS} non-empty answer choices.`),
        );
      }
    }
    return TeacherReportService.putTemplate({
      schooleId: effectiveSchoolId,
      title: templateTitle,
      questions: buildPayloadQuestions(list),
    });
  };

  const saveTemplate = () => {
    setTemplateSaving(true);
    persistTemplate(questions)
      .then((res) => {
        alert(res?.message || "Questionnaire saved.");
        loadTemplate();
      })
      .catch((e) => alert(e?.response?.data?.message || e.message || "Save failed"))
      .finally(() => setTemplateSaving(false));
  };

  const updateQuestion = (index, field, value) => {
    setQuestions((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      if (field === "text") next[index] = { ...next[index], text: value };
      return next;
    });
  };

  const updateOption = (qIndex, optIndex, value) => {
    setQuestions((prev) => {
      const next = [...prev];
      const opts = [...(next[qIndex].options || [])];
      opts[optIndex] = value;
      next[qIndex] = { ...next[qIndex], options: opts };
      return next;
    });
  };

  const addOption = (qIndex) => {
    setQuestions((prev) => {
      const next = [...prev];
      const opts = [...(next[qIndex].options || [])];
      opts.push("");
      next[qIndex] = { ...next[qIndex], options: opts };
      return next;
    });
  };

  const removeOption = (qIndex, optIndex) => {
    setQuestions((prev) => {
      const next = [...prev];
      const opts = [...(next[qIndex].options || [])];
      if (opts.length <= MIN_OPTIONS) return prev;
      opts.splice(optIndex, 1);
      next[qIndex] = { ...next[qIndex], options: opts };
      return next;
    });
  };

  const addQuestion = () => {
    setQuestions((prev) => [...prev, createDefaultQuestion(prev.length)]);
    setEditingFlags((prev) => [...prev, true]);
  };

  const requestRemoveQuestion = (q) => {
    if (questions.length <= MIN_Q) return;
    const label = q.text?.trim() || `Question ${(questions.findIndex((x) => x._clientId === q._clientId) + 1) || ""}`;
    setDeleteConfirm({
      open: true,
      clientId: q._clientId,
      label,
    });
  };

  const handleDeleteConfirm = () => {
    const clientId = deleteConfirm.clientId;
    setDeleteConfirm({ open: false, clientId: null, label: "" });

    if (!clientId || !effectiveSchoolId || questions.length <= MIN_Q) return;

    const removeIdx = questions.findIndex((q) => q._clientId === clientId);
    if (removeIdx < 0) return;

    const nextQuestions = questions
      .filter((q) => q._clientId !== clientId)
      .map((q, i) => ({ ...q, order: i }));
    const nextFlags = editingFlags.filter((_, i) => i !== removeIdx);

    setQuestions(nextQuestions);
    setEditingFlags(nextFlags);

    // If user deleted down to 0 questions, reflect immediately but don't auto-save (API requires >= 1 question).
    if (nextQuestions.length === 0) {
      setTemplate((prev) =>
        prev
          ? { ...prev, title: templateTitle, questions: [] }
          : { title: templateTitle, questions: [] },
      );
      alert("Question deleted. Add at least 1 question to save the questionnaire.");
      return;
    }

    setTemplateSaving(true);
    persistTemplate(nextQuestions)
      .then(() => {
        setTemplate((prev) =>
          prev
            ? { ...prev, title: templateTitle, questions: buildPayloadQuestions(nextQuestions) }
            : { title: templateTitle, questions: buildPayloadQuestions(nextQuestions) },
        );
        alert("Question deleted and saved.");
      })
      .catch((e) => {
        alert(e?.response?.data?.message || e.message || "Delete could not be saved. Reloading…");
        loadTemplate();
      })
      .finally(() => setTemplateSaving(false));
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ open: false, clientId: null, label: "" });
  };

  const setQuestionEditing = (index, editing) => {
    setEditingFlags((prev) => {
      const n = questions.length;
      const next = Array.from({ length: n }, (_, i) => (i < prev.length ? !!prev[i] : false));
      if (index >= 0 && index < n) next[index] = editing;
      return next;
    });
  };

  const openRate = (row) => {
    if (!effectiveSchoolId) {
      alert("School context is missing.");
      return;
    }
    TeacherReportService.getTemplate({ schooleId: effectiveSchoolId })
      .then((doc) => {
        const list = Array.isArray(doc.questions) ? doc.questions : [];
        if (!list.length) {
          alert("Questionnaire is not configured yet. Ask the school admin to set it up.");
          return;
        }
        setTemplate(doc);
        const existing = row.rating && Array.isArray(row.rating.answers) ? row.rating.answers : [];
        const init = list.map((_, idx) => {
          const hit = existing.find((a) => Number(a.questionIndex) === idx);
          return { questionIndex: idx, selectedOption: hit ? Number(hit.selectedOption) : 0 };
        });
        setRateStudent(row);
        setAnswers(init);
        setRateOpen(true);
      })
      .catch(() => {
        alert("Could not load questionnaire. Try again.");
      });
  };

  const saveRating = () => {
    if (!rateStudent || !effectiveSchoolId) return;
    TeacherReportService.putRating({
      schooleId: effectiveSchoolId,
      studentId: rateStudent.studentId,
      classId,
      sectionId: sectionId || null,
      periodKey,
      answers,
    })
      .then(() => {
        setRateOpen(false);
        setRateStudent(null);
        loadClassRatings();
        alert("Ratings saved.");
      })
      .catch((e) => alert(e?.response?.data?.message || e.message || "Save failed"));
  };

  if (!canEditTemplate && !canViewRatings) {
    return (
      <Box p={{ xs: 1, sm: 2 }}>
        <Typography className={pageClasses.pageTitle}>Student Assessments</Typography>
        <Typography style={{ color: "#6b7280", marginTop: 8 }}>
          This page is available to teachers and school administrators.
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={{ xs: 1, sm: 2 }}>
      <Box className={pageClasses.pageHeader}>
        <Box>
          <Typography className={pageClasses.pageTitle}>Student Assessments</Typography>
          <Typography className={pageClasses.pageSubtitle}>
            {canEditTemplate
              ? "Configure the rating questionnaire and manage assessment questions."
              : "Rate students using the school questionnaire for the selected academic year."}
          </Typography>
        </Box>
        {canEditTemplate && canViewRatings && (
          <Box display="flex" gridGap={8}>
            <Button
              variant={tab === "template" ? "contained" : "outlined"}
              className={tab === "template" ? pageClasses.primaryBtn : pageClasses.cancelBtn}
              onClick={() => setTab("template")}
            >
              Questionnaire
            </Button>
            <Button
              variant={tab === "ratings" ? "contained" : "outlined"}
              className={tab === "ratings" ? pageClasses.primaryBtn : pageClasses.cancelBtn}
              onClick={() => setTab("ratings")}
            >
              Student ratings
            </Button>
          </Box>
        )}
      </Box>

      {noteOpen && (
        <Box className={pageClasses.noteBanner}>
          <Box className={pageClasses.noteIconWrap}>
            <InfoOutlinedIcon style={{ fontSize: 18 }} />
          </Box>
          <Box flex={1}>
            <Typography style={{ fontWeight: 600, color: "#1f3a5f", fontSize: "0.9rem" }}>
              {canEditTemplate ? "Questionnaire setup" : "Student ratings"}
            </Typography>
            <Typography style={{ color: "#46566b", fontSize: "0.85rem", marginTop: 2 }}>
              {canEditTemplate
                ? "Each row is one question shown to teachers. Enter question text and answer choices, then save the questionnaire."
                : "Select class, section, and academic year, then rate each student using the configured questionnaire."}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setNoteOpen(false)}>
            <CloseIcon style={{ fontSize: 18, color: "#7a8aa0" }} />
          </IconButton>
        </Box>
      )}

      {tab === "template" && canEditTemplate && (
        <Card elevation={0} className={pageClasses.contentCard}>
            {role === "SUPERADMIN" && (
              <Box mb={2}>
                <Typography className={pageClasses.fieldLabel}>
                  <BusinessIcon style={{ fontSize: 16 }} /> School
                </Typography>
                <FormControl fullWidth variant="outlined" size="small">
                  <Select
                    displayEmpty
                    value={superAdminSchoolId}
                    onChange={(e) => setSuperAdminSchoolId(e.target.value)}
                  >
                    <MenuItem value=""><em>Select school</em></MenuItem>
                    {schoolList.map((s) => (
                      <MenuItem key={s._id} value={s._id}>
                        {s.orgName || s.schoolName || s._id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
            <Typography variant="body2" color="textSecondary" paragraph>
              Each row is one question shown to teachers. Enter the question text and answer choices (use + / − to add or remove choices). Teachers pick one option per question for each student. To save: at least {MIN_Q_TO_SAVE} question required; each question needs at least {MIN_OPTIONS} choices.
            </Typography>
            {templateLoading && (
              <Box display="flex" alignItems="center" gridGap={8} py={2}>
                <CircularProgress size={20} />
                <Typography>Loading…</Typography>
              </Box>
            )}
            {!templateLoading && !effectiveSchoolId && (
              <Typography color="error">Select a school to edit the questionnaire.</Typography>
            )}
            {!templateLoading && effectiveSchoolId && questions.length === 0 ? (
              <Box mb={2} p={2} style={{ border: "1px dashed #cfd8dc", borderRadius: 8, background: "#fafafa" }}>
                <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 6 }}>
                  No questions yet
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Add your first question using the button below.
                </Typography>
                <Box className={templateClasses.addQuestionRow} style={{ marginBottom: 0, marginTop: 12 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={addQuestion}
                    disabled={templateLoading}
                  >
                    Add question
                  </Button>
                </Box>
              </Box>
            ) : null}

            {questions.map((q, qi) => {
              const isEditing = editingFlags[qi] === true;
              return (
              <Box key={q._clientId} mb={2} p={2} style={{ border: "1px solid #e0e0e0", borderRadius: 8 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 8 }} flexWrap="wrap">
                  <Typography variant="subtitle2">Question {qi + 1}</Typography>
                  <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                    {isEditing ? (
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => setQuestionEditing(qi, false)}
                        disabled={templateLoading}
                        style={{ backgroundColor: "rgb(15 59 98)", color: "#fff" }}
                      >
                        Done
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<EditIcon />}
                        onClick={() => setQuestionEditing(qi, true)}
                        disabled={templateLoading}
                      >
                        Edit
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      type="button"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={() => requestRemoveQuestion(q)}
                      disabled={questions.length <= MIN_Q || templateLoading}
                    >
                      Delete
                    </Button>
                  </Box>
                </Box>
                {isEditing ? (
                  <>
                    <TextField
                      fullWidth
                      label="Question text"
                      value={q.text || ""}
                      onChange={(e) => updateQuestion(qi, "text", e.target.value)}
                      variant="outlined"
                      margin="dense"
                    />
                    <Box className={templateClasses.optionsHeader}>
                      <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                        Answer choices
                      </Typography>
                      <Box className={templateClasses.optionsToolbar}>
                        <IconButton
                          aria-label="Add answer choice"
                          size="small"
                          className={templateClasses.optionAddBtn}
                          onClick={() => addOption(qi)}
                          disabled={templateLoading}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    {(q.options || []).map((opt, oi) => (
                      <Box key={`${q._clientId}-opt-${oi}`} className={templateClasses.optionRow}>
                        <TextField
                          className={templateClasses.optionField}
                          fullWidth
                          label={`Choice ${oi + 1}`}
                          value={opt || ""}
                          onChange={(e) => updateOption(qi, oi, e.target.value)}
                          variant="outlined"
                          margin="dense"
                          size="small"
                        />
                        <IconButton
                          aria-label={`Remove choice ${oi + 1}`}
                          size="small"
                          className={templateClasses.optionRemoveBtn}
                          onClick={() => removeOption(qi, oi)}
                          disabled={(q.options || []).length <= MIN_OPTIONS || templateLoading}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </>
                ) : (
                  <Box style={{ paddingTop: 4 }}>
                    <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8 }}>
                      {q.text?.trim() ? q.text : "—"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 6 }}>
                      Answer choices
                    </Typography>
                    <Box>
                      {(q.options || []).map((opt, oi) => (
                        <span key={`${q._clientId}-view-${oi}`} className={templateClasses.choiceChip}>
                          {opt?.trim() ? opt : "—"}
                        </span>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
              );
            })}
            {!templateLoading && effectiveSchoolId && questions.length > 0 ? (
              <Box className={templateClasses.addQuestionRow}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={addQuestion}
                  disabled={templateLoading}
                >
                  Add question
                </Button>
              </Box>
            ) : null}
            <Box display="flex" gap={1} flexWrap="wrap" mt={2}>
              <Button
                variant="contained"
                className={pageClasses.primaryBtn}
                onClick={saveTemplate}
                disabled={!effectiveSchoolId || templateSaving || templateLoading}
              >
                {templateSaving ? "Saving…" : "Save questionnaire"}
              </Button>
            </Box>
        </Card>
      )}

      {tab === "ratings" && canViewRatings && (
        <>
          <Card elevation={0} className={pageClasses.filtersCard}>
            <Box className={pageClasses.filtersTopRow}>
              <span className={pageClasses.filtersChip} onClick={() => setFiltersOpen((v) => !v)}>
                <FilterListIcon style={{ fontSize: 18 }} />
                Filters
                {activeFilterCount > 0 && <span className={pageClasses.countBadge}>{activeFilterCount}</span>}
                {filtersOpen ? <ExpandMoreIcon style={{ fontSize: 18 }} /> : <ChevronRightIcon style={{ fontSize: 18 }} />}
              </span>
            </Box>

            <Collapse in={filtersOpen}>
              <Grid container spacing={2} style={{ marginTop: 8 }}>
                {role === "SUPERADMIN" && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography className={pageClasses.fieldLabel}>
                      <BusinessIcon style={{ fontSize: 16 }} /> School
                    </Typography>
                    <FormControl fullWidth variant="outlined" size="small">
                      <Select
                        displayEmpty
                        value={superAdminSchoolId}
                        onChange={(e) => {
                          setSuperAdminSchoolId(e.target.value);
                          setClassId("");
                          setSectionId("");
                        }}
                      >
                        <MenuItem value=""><em>Select school</em></MenuItem>
                        {schoolList.map((s) => (
                          <MenuItem key={s._id} value={s._id}>
                            {s.orgName || s.schoolName || s._id}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {(role === "SCHOOLE" || role === "SUPERADMIN" || role === "TEACHER") && (
                  <>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className={pageClasses.fieldLabel}>
                        <SchoolIcon style={{ fontSize: 16 }} /> Class
                      </Typography>
                      <FormControl fullWidth variant="outlined" size="small">
                        <Select
                          displayEmpty
                          value={classId}
                          onChange={isTeacher ? handleClassChange : (e) => {
                            setClassId(e.target.value);
                            setSectionId("");
                          }}
                          disabled={isTeacher && classList.length <= 1}
                        >
                          {!isTeacher && <MenuItem value=""><em>Select class</em></MenuItem>}
                          {classList.map((c) => (
                            <MenuItem key={c._id} value={c._id}>{c.className}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className={pageClasses.fieldLabel}>
                        <SchoolIcon style={{ fontSize: 16 }} /> Section
                      </Typography>
                      <FormControl fullWidth variant="outlined" size="small">
                        <Select
                          displayEmpty
                          value={sectionId}
                          onChange={isTeacher ? handleSectionChange : (e) => setSectionId(e.target.value)}
                          disabled={!classId || sectionList.length === 0}
                        >
                          <MenuItem value=""><em>All sections</em></MenuItem>
                          {sectionList.map((s) => (
                            <MenuItem key={s._id} value={s._id}>{s.sectionName}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}
                <Grid item xs={12} sm={6} md={4}>
                  <Typography className={pageClasses.fieldLabel}>Academic Year</Typography>
                  <FormControl fullWidth variant="outlined" size="small" disabled={!effectiveSchoolId || academicYearOptions.length === 0}>
                    <Select
                      displayEmpty
                      value={periodKey && academicYearOptions.includes(periodKey) ? periodKey : ""}
                      onChange={(e) => setPeriodKey(e.target.value)}
                    >
                      {academicYearOptions.length === 0 ? (
                        <MenuItem value="" disabled>
                          <em>No active year — add in Academic Master</em>
                        </MenuItem>
                      ) : (
                        academicYearOptions.map((y) => (
                          <MenuItem key={y} value={y}>{y}</MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                  {effectiveSchoolId && academicYearOptions.length === 0 && (
                    <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 4 }}>
                      Add an academic year with status Active under Academic Master.
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box display="flex" alignItems="flex-end" height="100%" pb={0.5}>
                    <Button
                      variant="contained"
                      className={pageClasses.primaryBtn}
                      startIcon={<RefreshIcon />}
                      onClick={loadClassRatings}
                      disabled={!effectiveSchoolId || !classId || !periodKey}
                    >
                      Refresh list
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Collapse>
          </Card>

          <Card elevation={0} className={ratingsTableClasses.tableCard}>
            <Box display="flex" alignItems="center" justifyContent="space-between" p={2} style={{ borderBottom: "1px solid #eef1f5" }}>
              <Typography style={{ fontWeight: 700, color: "#0f2d4d" }}>
                {rows.length} Student{rows.length !== 1 ? "s" : ""}
              </Typography>
            </Box>
            <TableContainer component={Paper} elevation={0}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell className={ratingsTableClasses.headCell}>Student</TableCell>
                    <TableCell className={ratingsTableClasses.headCell}>Status</TableCell>
                    <TableCell className={ratingsTableClasses.headCell} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listLoading && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" style={{ padding: 32, color: "#7a8aa0" }}>
                        <CircularProgress size={24} style={{ marginRight: 8 }} />
                        Loading…
                      </TableCell>
                    </TableRow>
                  )}
                  {!listLoading && rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" style={{ padding: 32, color: "#7a8aa0" }}>
                        No students for this filter.
                      </TableCell>
                    </TableRow>
                  )}
                  {!listLoading &&
                    rows.map((r) => (
                      <TableRow key={String(r.studentId)} hover>
                        <TableCell className={ratingsTableClasses.bodyCell}>
                          <Typography style={{ fontWeight: 600, color: "#0f2d4d" }}>{r.studentName}</Typography>
                        </TableCell>
                        <TableCell className={ratingsTableClasses.bodyCell}>
                          <Chip
                            size="small"
                            label={r.rating ? "Rated" : "Not rated"}
                            className={r.rating ? ratingsTableClasses.ratedChip : ratingsTableClasses.notRatedChip}
                          />
                        </TableCell>
                        <TableCell className={ratingsTableClasses.bodyCell} align="right">
                          {canSubmitRating ? (
                            r.rating ? (
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={() => openRate(r)}
                                className={pageClasses.primaryBtn}
                              >
                                Edit rating
                              </Button>
                            ) : (
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<RateReviewIcon />}
                                onClick={() => openRate(r)}
                                style={{ backgroundColor: "#4CAF50", color: "#fff", textTransform: "none", fontWeight: 600, borderRadius: 8 }}
                              >
                                Rate
                              </Button>
                            )
                          ) : (
                            <Typography variant="caption" color="textSecondary">—</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </>
      )}

      <Dialog open={deleteConfirm.open} onClose={handleDeleteCancel} scroll="paper" PaperProps={{ className: pageClasses.dialogPaper }}>
        <Box className={pageClasses.dialogTitleBar}>
          <span className={pageClasses.dialogTitleText}>Delete Question?</span>
          <IconButton size="small" onClick={handleDeleteCancel}>
            <CloseIcon style={{ fontSize: 20, color: "#7a8aa0" }} />
          </IconButton>
        </Box>
        <DialogContent className={pageClasses.dialogBody}>
          <Typography variant="body2">
            Are you sure you want to delete{" "}
            <strong>{deleteConfirm.label || "this question"}</strong>?
            Deleting saves the questionnaire immediately.
          </Typography>
          {questions.length === 1 && (
            <Typography variant="caption" color="error" display="block" style={{ marginTop: 12 }}>
              This will leave 0 questions. You must add at least 1 question before saving.
            </Typography>
          )}
        </DialogContent>
        <DialogActions className={pageClasses.dialogFooter}>
          <Button onClick={handleDeleteCancel} className={pageClasses.cancelBtn}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            className={pageClasses.saveBtn}
            disabled={templateSaving}
            style={{ backgroundColor: "#c62828" }}
          >
            {templateSaving ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rateOpen} onClose={() => setRateOpen(false)} scroll="paper" fullWidth maxWidth="md" PaperProps={{ className: pageClasses.dialogPaper }}>
        <Box className={pageClasses.dialogTitleBar}>
          <span className={pageClasses.dialogTitleText}>
            Ratings — {rateStudent?.studentName || "Student"} ({periodKey})
          </span>
          <IconButton size="small" onClick={() => setRateOpen(false)}>
            <CloseIcon style={{ fontSize: 20, color: "#7a8aa0" }} />
          </IconButton>
        </Box>
        <DialogContent className={pageClasses.dialogBody}>
          {template &&
            template.questions &&
            template.questions.map((q, idx) => (
              <Box key={idx} mb={2}>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend" style={{ fontWeight: 600, color: "#0f2d4d", marginBottom: 8 }}>
                    {idx + 1}. {q.text}
                  </FormLabel>
                  <RadioGroup
                    row
                    value={String((answers[idx] && answers[idx].selectedOption) ?? 0)}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setAnswers((prev) => {
                        const next = [...prev];
                        next[idx] = { questionIndex: idx, selectedOption: v };
                        return next;
                      });
                    }}
                  >
                    {(q.options || []).map((label, oi) => (
                      <FormControlLabel key={oi} value={String(oi)} control={<Radio color="primary" />} label={label} />
                    ))}
                  </RadioGroup>
                </FormControl>
                {idx < template.questions.length - 1 && <Divider style={{ marginTop: 12 }} />}
              </Box>
            ))}
        </DialogContent>
        <DialogActions className={pageClasses.dialogFooter}>
          <Button onClick={() => setRateOpen(false)} className={pageClasses.cancelBtn}>Cancel</Button>
          <Button onClick={saveRating} variant="contained" className={pageClasses.saveBtn}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
